import { ref } from 'vue';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { isNativeApp, sessionToken } from '@/lib/native';

/**
 * Verrouillage de l'app par Face ID, Touch ID ou empreinte (code de l'appareil
 * en secours). Réglage propre à l'appareil, gardé dans le trousseau. Verrouille
 * au lancement et au retour après plus d'une minute en arrière-plan : les
 * factures et l'IBAN ne restent pas lisibles sur un téléphone posé.
 */
const LOCK_KEY = 'biometric-lock';
const RELOCK_AFTER_MS = 60_000;

export const lockEnabled = ref(false);
export const locked = ref(false);
/** « Face ID », « Touch ID », « l'empreinte »… ; vide si rien n'est configuré. */
export const biometryLabel = ref('');

let backgroundedAt: number | null = null;

async function plugin() {
  return import('@aparajita/capacitor-biometric-auth');
}

async function readBiometry(): Promise<void> {
  const { BiometricAuth, BiometryType } = await plugin();
  const info = await BiometricAuth.checkBiometry();
  const labels: Partial<Record<number, string>> = {
    [BiometryType.faceId]: 'Face ID',
    [BiometryType.touchId]: 'Touch ID',
    [BiometryType.fingerprintAuthentication]: 'l’empreinte digitale',
    [BiometryType.faceAuthentication]: 'la reconnaissance faciale',
  };
  biometryLabel.value = info.isAvailable
    ? labels[info.biometryType] ?? 'la biométrie'
    : info.deviceIsSecure
      ? 'le code de l’appareil'
      : '';
}

/** Demande l'authentification ; `true` si elle réussit. */
export async function authenticate(reason: string): Promise<boolean> {
  const { BiometricAuth } = await plugin();
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Annuler',
      allowDeviceCredential: true,
      iosFallbackTitle: 'Utiliser le code',
      androidTitle: 'Déverrouiller Yakafa',
      androidSubtitle: reason,
    });
    return true;
  } catch {
    return false;
  }
}

export async function unlock(): Promise<void> {
  if (await authenticate('Déverrouillez Yakafa pour accéder à vos factures')) locked.value = false;
}

export async function setLockEnabled(enabled: boolean): Promise<boolean> {
  // Activer comme désactiver exige de prouver qu'on est bien le propriétaire.
  if (!(await authenticate(enabled ? 'Activer le verrouillage de Yakafa' : 'Désactiver le verrouillage de Yakafa'))) {
    return false;
  }
  lockEnabled.value = enabled;
  await (enabled ? SecureStorage.set(LOCK_KEY, '1') : SecureStorage.remove(LOCK_KEY));
  return true;
}

/** Au démarrage de l'app : lit le réglage et verrouille si une session existe. */
export async function setupAppLock(): Promise<void> {
  if (!isNativeApp) return;
  await readBiometry().catch(() => undefined);
  lockEnabled.value = ((await SecureStorage.get(LOCK_KEY).catch(() => null)) as string | null) === '1';
  if (lockEnabled.value && sessionToken()) {
    locked.value = true;
    void unlock();
  }

  const { App } = await import('@capacitor/app');
  await App.addListener('appStateChange', ({ isActive }) => {
    if (!isActive) {
      backgroundedAt = Date.now();
      return;
    }
    const away = backgroundedAt ? Date.now() - backgroundedAt : 0;
    backgroundedAt = null;
    if (lockEnabled.value && sessionToken() && away > RELOCK_AFTER_MS) {
      locked.value = true;
      void unlock();
    }
  });
}
